import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  model,
  OnDestroy,
  ViewChild
} from '@angular/core';
import { FormGroup, FormsModule } from '@angular/forms';
import { FieldType } from '@ngx-formly/core';
import { TranslatePipe } from '@ngx-translate/core';
import { TabsModule } from 'primeng/tabs';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { Product } from '../../../../generated/generated-types';
import { TextareaSizeStorageService } from '../../../../shared/services/textarea-size-storage.service';

interface TextareaResizeSession {
  textarea: HTMLTextAreaElement;
  storageKey: string;
  startHeight: number;
}

@Component({
  selector: 'app-product-text-widget',
  templateUrl: './product-text-widget.component.html',
  styleUrls: ['./product-text-widget.component.scss'],
  //for now only works with default - to always on every load get values in UI directly
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    CommonModule,
    TabsModule,
    TooltipModule,
    TranslatePipe,
    FormsModule,
    TextareaModule
  ]
})
export class ProductTextWidgetComponent extends FieldType implements AfterViewInit, OnDestroy {
  @ViewChild('ta1') ta1?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('ta2') ta2?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('ta3') ta3?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('ta4') ta4?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('ta5') ta5?: ElementRef<HTMLTextAreaElement>;

  selectedTab = model<number>(0);
  cdr = inject(ChangeDetectorRef);

  private readonly textareaSizeStorage = inject(TextareaSizeStorageService);
  private activeResizeSession: TextareaResizeSession | null = null;
  private documentPointerUpAttached = false;
  private readonly listenerDisposers: (() => void)[] = [];

  private readonly textareaConfigs: readonly { ref: () => ElementRef<HTMLTextAreaElement> | undefined; storageKey: string }[] = [
    { ref: () => this.ta1, storageKey: 'ProductTextWidgetComponent:salesText' },
    { ref: () => this.ta2, storageKey: 'ProductTextWidgetComponent:purchaseText' },
    { ref: () => this.ta3, storageKey: 'ProductTextWidgetComponent:dimensionText' },
    { ref: () => this.ta4, storageKey: 'ProductTextWidgetComponent:informationText' },
    { ref: () => this.ta5, storageKey: 'ProductTextWidgetComponent:infoPopUp' },
  ];

  private readonly onDocumentPointerUp = (): void => {
    if (!this.activeResizeSession) {
      return;
    }

    const { textarea, storageKey, startHeight } = this.activeResizeSession;
    this.activeResizeSession = null;

    if (!document.body.contains(textarea)) {
      return;
    }

    const endHeight = Math.round(textarea.getBoundingClientRect().height);
    // 2px threshold: getBoundingClientRect() returns sub-pixel values; after Math.round(),
    // two reads of the same untouched element can differ by 1px due to browser zoom,
    // HiDPI scaling, or CSS sub-pixel rendering. 2px filters rounding noise.
    if (endHeight > 0 && Math.abs(endHeight - startHeight) > 2) {
      this.textareaSizeStorage.saveSize(storageKey, endHeight);
    }
  };

  get product() {
    return this.model as Product;
  }

  ngAfterViewInit(): void {
    if (!this.props?.persistSize) {
      return;
    }

    for (const config of this.textareaConfigs) {
      const textarea = config.ref()?.nativeElement;
      if (textarea) {
        this.setupTextareaPersistence(textarea, config.storageKey);
      }
    }
  }

  ngOnDestroy(): void {
    for (const dispose of this.listenerDisposers) {
      dispose();
    }
    this.listenerDisposers.length = 0;
    this.activeResizeSession = null;
    this.documentPointerUpAttached = false;
  }

  onTextChange(field: keyof Product, value: string) {
    this.model[field] = value;
    (this.form as FormGroup).patchValue({
      [field]: this.model[field]
    });
  }

  private setupTextareaPersistence(textarea: HTMLTextAreaElement, storageKey: string): void {
    const height = this.textareaSizeStorage.getClampedHeight(storageKey);
    if (height) {
      textarea.style.setProperty('height', `${height}px`, 'important');
    }

    const onPointerDown = (): void => {
      this.activeResizeSession = {
        textarea,
        storageKey,
        startHeight: Math.round(textarea.getBoundingClientRect().height)
      };
    };

    textarea.addEventListener('pointerdown', onPointerDown);
    this.listenerDisposers.push(() => textarea.removeEventListener('pointerdown', onPointerDown));

    if (!this.documentPointerUpAttached) {
      document.addEventListener('pointerup', this.onDocumentPointerUp);
      this.documentPointerUpAttached = true;
      this.listenerDisposers.push(() => document.removeEventListener('pointerup', this.onDocumentPointerUp));
    }
  }
}
